//core
import React from 'react';
import * as Yup from 'yup';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import Color from 'color';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Ramda from 'ramda';

//mui
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Grid,
  Paper,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { styled } from '@mui/styles';

//components
import CbButton from '../../../../../components/Buttons/CbButton';
import { Show } from '../../../../../components/Show';
import TextFieldBase from '../../../../../components/inputs/text-fields/base/TextFieldBase';
import { Flexbox } from '../../../../../components/layout/Flexbox';

//hocs
import { withFormController } from '../../../../../components/hocs/forms';

interface SpecifiedEventExclusionModalProps {
  data: {
    onSubmit: (formValues: SEFormValues) => void;
    exclusions: SpecifiedEventExclusion[];
  };
  close: () => void;
}

export const SpecifiedEventExclusionModal = ({
  data: modalProps,
  close,
}: SpecifiedEventExclusionModalProps) => {
  const scrollableRef = React.useRef<null | HTMLDivElement>(null);
  const formMethods = useForm<SEFormValues>({
    defaultValues: {
      exclusions: modalProps.exclusions,
    },
    resolver: yupResolver(schema),
  });

  const fieldArrayMethods = useFieldArray({
    control: formMethods.control,
    name: 'exclusions',
  });

  const handleAddSE = ({ scroll }: { scroll: boolean }) => {
    fieldArrayMethods.append({
      claimNumber: '',
      claimName: '',
      claimDate: '',
      claimDescription: '',
    });

    if (scroll) {
      setTimeout(
        () => scrollableRef.current?.scrollIntoView({ behavior: 'smooth' }),
        300
      );
    }
  };

  React.useEffect(() => {
    /* When component first mounts, check if at least one SE exists. If so, dont add a card by default */
    if (formMethods.getValues().exclusions[0]?.claimNumber) {
      return;
    }

    handleAddSE({ scroll: false });
  }, []);

  return (
    <FormProvider {...formMethods}>
      <form
        onSubmit={formMethods.handleSubmit(
          Ramda.compose(close, modalProps.onSubmit)
        )}
        noValidate
      >
        <DialogContent style={{ padding: '1rem 2.5rem' }}>
          <Show when={!fieldArrayMethods.fields.length}>
            <Box p={4} textAlign="center">
              <Typography variant="body1" color="primary">
                No Specified Event Exclusions
              </Typography>
            </Box>
          </Show>

          {fieldArrayMethods.fields.map((field, index) => {
            return (
              <CardContainer key={field.id} elevation={3}>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <TextField
                      label="Incident Number / Claim Number"
                      required
                      name={`exclusions.${index}.claimNumber`}
                    />
                  </Grid>

                  <Grid item xs={4}>
                    <TextField
                      label="Incident Claim / Claim Name / Claimant"
                      required
                      name={`exclusions.${index}.claimName`}
                    />
                  </Grid>

                  <Grid item xs={4}>
                    <TextField
                      type="date"
                      label="Date"
                      required
                      name={`exclusions.${index}.claimDate`}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      minRows={4}
                      multiline
                      label="Incident / Claim Description (internal use only)"
                      name={`exclusions.${index}.claimDescription`}
                    />
                  </Grid>
                </Grid>
                <Button
                  className="contrast-text"
                  style={{ position: 'absolute', top: 5, right: 5 }}
                  onClick={() => fieldArrayMethods.remove(index)}
                >
                  Remove
                </Button>
              </CardContainer>
            );
          })}
          <div ref={scrollableRef} />
        </DialogContent>
        <DialogActions>
          <Flexbox>
            <Box>
              <Button
                startIcon={<AddIcon style={{ pointerEvents: 'none' }} />}
                className="contrast-text"
                onClick={() => handleAddSE({ scroll: true })}
              >
                Add SE
              </Button>
            </Box>

            <Box>
              <CbButton onClick={close} styleName="cancel">
                Cancel
              </CbButton>
              <CbButton type="submit" styleName="ctaButton">
                Save
              </CbButton>
            </Box>
          </Flexbox>
        </DialogActions>
      </form>
    </FormProvider>
  );
};

export default SpecifiedEventExclusionModal;

export const SpecifiedEventExclusionModalConfig = {
  SpecifiedEventExclusionModal: {
    component: SpecifiedEventExclusionModal,
    config: {
      title: 'Specified Event Exclusion Endorsement (SE)',
      fullWidth: true,
      maxWidth: 'md',
    },
  },
};

//controlled inputs
const TextField = withFormController(TextFieldBase);

//styling
const CardContainer = styled(Paper)(({ theme }) => {
  return {
    background:
      theme.palette.mode === 'dark'
        ? Color(theme.config.colors.darkGreyActive).darken(0.3).toString()
        : Color(theme.palette.background.active).lighten(0.065).toString(),
    padding: '2rem',
    borderRadius: 5,
    marginBottom: '2rem',
    position: 'relative',
  };
});

//validations
const schema = Yup.object().shape({
  exclusions: Yup.array().of(
    Yup.object().shape({
      claimNumber: Yup.string().trim().label('Claim number').required(),
      claimName: Yup.string().trim().label('Claim name').required(),
      claimDate: Yup.string().label('Claim date').required(),
    })
  ),
});

//types
export type SEFormValues = {
  exclusions: SpecifiedEventExclusion[];
};

export type SpecifiedEventExclusion = {
  claimNumber: string;
  claimName: string;
  claimDate: string;
  claimDescription?: string;
};
